/**
 * The approval queue.
 *
 * This screen is where the blocked loss-exit policy becomes visible to a
 * person, so it is built to be hard to ignore rather than tidy:
 *
 * - Oldest first, with the age stated in days. A request that has sat for
 *   three weeks should look wrong.
 * - Today's loss is shown next to the loss at filing, because the decision is
 *   about the position as it is now.
 * - A note is mandatory before either button works. An override with no stated
 *   reason is not reviewable later, and declining is a decision too.
 * - Approving and declining are equally prominent. Making "hold" the easy
 *   default would quietly turn the policy into paralysis.
 * - Approving sells: the whole position, straight away, no lower than 2% under
 *   the best bid. The result is followed and shown here, as it is for an
 *   emergency exit.
 */
import { useCallback, useEffect, useState } from 'react';

import { approveRequest, declineRequest, fetchApprovals } from '../../api/endpoints';
import type { ApprovalRequestItem, ControlRequestRow } from '../../api/types';
import { Notice } from '../../components/Notice';
import { RequirePermission } from '../../components/RequirePermission';
import { describe as describeError } from '../../lib/errors';
import { describeSale, waitForControlRequest } from '../../lib/controlRequests';
import { formatMoney, formatQuantity } from '../../lib/format';

function Age({ days }: { days: number }) {
  const tone = days >= 30 ? 'loss' : days >= 7 ? 'warn' : 'muted';
  return (
    <span className={`age age--${tone}`} title={`Filed ${days} days ago`}>
      {days === 0 ? 'today' : `${days}d old`}
    </span>
  );
}

function Money({ value }: { value: string | null }) {
  if (value === null) return <span className="num muted">—</span>;
  const negative = value.trim().startsWith('-');
  return (
    <span className={`num ${negative ? 'loss' : 'gain'}`}>{formatMoney(value)}</span>
  );
}

export function ApprovalsPage() {
  const [items, setItems] = useState<ApprovalRequestItem[]>([]);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState<{ symbol: string; result: ControlRequestRow | null } | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      setItems(await fetchApprovals('OPEN'));
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: number, approve: boolean) {
    const note = (notes[id] ?? '').trim();
    if (!note) {
      setError('Explain the decision before approving or declining.');
      return;
    }
    setBusy(id);
    try {
      if (approve) {
        const decided = await approveRequest(id, note);
        if (decided.sale_request_id) {
          setSale({ symbol: decided.symbol, result: null });
          await load();
          const result = await waitForControlRequest(decided.sale_request_id);
          setSale({ symbol: decided.symbol, result });
        }
      } else {
        await declineRequest(id, note);
      }
      await load();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <h1>Approvals</h1>
        <p className="page__lead">
          Positions the loss-exit policy will not sell on its own. A position is brought here
          when it falls 3.5% below its entry after fees, or when its strategy wants out at a
          loss. <strong>Sell now</strong> sells the whole position straight away, no lower than
          2% under the best bid;{' '}
          <strong>Keep holding</strong> leaves it waiting for its take-profit.
        </p>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {sale ? (
        <Notice tone={sale.result && sale.result.state !== 'DONE' ? 'error' : 'info'}>
          Selling {sale.symbol}:{' '}
          {sale.result === null
            ? 'queued; the scheduler places it within a few seconds…'
            : sale.result.state === 'DONE'
              ? describeSale(sale.result.result)
              : sale.result.state === 'PENDING'
                ? 'still queued; check Orders in a minute'
                : `failed: ${String(sale.result.result?.error ?? 'unknown error')}`}
        </Notice>
      ) : null}

      {loading ? <p className="muted">Loading…</p> : null}

      {!loading && items.length === 0 ? (
        <Notice tone="info">Nothing is waiting for a decision.</Notice>
      ) : null}

      <ul className="approvals">
        {items.map((item) => (
          <li
            key={item.id}
            className={`approval approval--${item.severity.toLowerCase()}`}
            data-testid={`approval-${item.id}`}
          >
            <div className="approval__head">
              <span className="approval__symbol">{item.symbol}</span>
              <span className="approval__exchange">{item.exchange}</span>
              <span className="approval__kind">{item.kind.replace('_', ' ').toLowerCase()}</span>
              <Age days={item.age_days} />
            </div>

            <p className="approval__reason">{item.reason}</p>

            <dl className="approval__figures">
              <div>
                <dt>Unrealised now</dt>
                <dd>
                  <Money value={item.current?.unrealised ?? null} />
                  {item.current?.unrealised_pct ? (
                    <span className="muted"> ({formatQuantity(item.current.unrealised_pct, 1)}%)</span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>When filed</dt>
                <dd>
                  <Money value={item.unrealised_at_filing} />
                </dd>
              </div>
              <div>
                <dt>Held</dt>
                <dd className="num">{item.current?.held_days ?? item.held_days ?? '—'} days</dd>
              </div>
              <div>
                <dt>Quantity</dt>
                <dd className="num">
                  {item.current?.qty ? formatQuantity(item.current.qty, 8) : '—'}
                </dd>
              </div>
            </dl>

            <label className="approval__note">
              <span>Why? (recorded in the audit log)</span>
              <textarea
                rows={2}
                value={notes[item.id] ?? ''}
                onChange={(event) =>
                  setNotes((current) => ({ ...current, [item.id]: event.target.value }))
                }
                placeholder="State the reasoning for whichever choice you make."
              />
            </label>

            <div className="approval__actions">
              <RequirePermission permission="danger:loss_exit_override">
                <button
                  type="button"
                  className="button button--danger"
                  disabled={busy === item.id}
                  onClick={() => void decide(item.id, true)}
                >
                  Sell now
                </button>
              </RequirePermission>
              <RequirePermission permission="control:trading">
                <button
                  type="button"
                  className="button"
                  disabled={busy === item.id}
                  onClick={() => void decide(item.id, false)}
                >
                  Keep holding
                </button>
              </RequirePermission>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
